import React, { Fragment, useState, useEffect, useRef } from "react";
import { VersionBanner, FRONTEND_VERSION } from "../VersionBanner";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, Link } from "react-router-dom";
import ApplicationLogo from "../Logos/ApplicationLogo";

import Echo from "laravel-echo";
import Pusher from "pusher-js";

import { Disclosure, Dialog, Transition } from "@headlessui/react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes, faBooks, faBug } from "@fortawesome/pro-regular-svg-icons";
import { faFileChartColumn, faCogs, faSearch, faMeterBolt, faAt, faBuildingCircleCheck, faCalendar, faBagShopping, faUser, faRobot, faBuilding } from "@fortawesome/pro-regular-svg-icons";
import * as ProRegularIcons from "@fortawesome/pro-regular-svg-icons";
import * as ProSolidIcons from "@fortawesome/pro-solid-svg-icons";

import axios from "axios";
import { getStaff, signOutUser } from "../../store/auth/authActions";
import { getTemplateCategories, getTemplates, getWhatsappTemplates } from "../../store/template/templateActions";
import AppLoader from "./AppLoader";
import { getTags } from "../../store/tag/tagActions";

import ProfilePicture from "../Auth/ProfilePicture";
import StaffOnline from "../Auth/StaffOnline";
import { getBooking, getFlaggedIds } from "../../store/booking/bookingActions";
import { getProperties } from "../../store/property/propertyActions";

import { openModal } from "../../store/modal/modalReducer";
import { faMessagePlus } from "@fortawesome/pro-solid-svg-icons";
import { getFolders } from "../../store/tag/folderActions";
import { getCollections } from "../../store/collection/collectionActions";
import { getAppointments } from "../../store/appointments/appointmentActions";
import { getManagers } from "../../store/managers/managersActions";
import { getSettings } from "../../store/settings/settingsActions";
import { getChatTags } from "../../store/chatTag/chatTagActions";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export default function NavBarDynamic() {
    // ==================== STATE ====================
    
    // Dynamic navbar structure (from API)
    const [navbarFolders, setNavbarFolders] = useState([]);
    const [underPropertyFolders, setUnderPropertyFolders] = useState([]);
    const [navbarProperties, setNavbarProperties] = useState([]);
    const [folderTags, setFolderTags] = useState({});
    const [folderPropertyAssignments, setFolderPropertyAssignments] = useState({});
    
    // Real-time counts (from websocket - same as original NavBar)
    const [folderCounts, setFolderCounts] = useState({});
    const [tagCounts, setTagCounts] = useState({});
    const [propertyCounts, setPropertyCounts] = useState({});
    
    // Orphaned bookings count
    const [orphanedCount, setOrphanedCount] = useState(0);
    const [orphanedRedCount, setOrphanedRedCount] = useState(0);
    const [orphanedOrangeCount, setOrphanedOrangeCount] = useState(0);
    
    // Mentions
    const [totalMention, setTotalMention] = useState("0 | 0");
    
    // Chat Tags
    const [chatTagCounts, setChatTagCounts] = useState({});
    
    // UI state
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [mbBalance, setMbBalance] = useState("");

    // ==================== HOOKS ====================
    
    let location = useLocation();
    const dispatch = useDispatch();

    const { staff, current_staff, login_method } = useSelector((state) => state.auth);
    const { settings } = useSelector((state) => state.settings);
    const { properties } = useSelector((state) => state.properties);
    const { chatTags = [] } = useSelector((state) => state.chatTags || {});

    // URL params
    var url_string = window.location.href;
    var url = new URL(url_string);
    var tag_id = url.searchParams.get("tag");
    var property_id = url.searchParams.get("property_id");

    // Echo refs
    const echoRef = useRef(null);
    const debounceRef = useRef({ channels: 0, tags: 0 });
    const DEBOUNCE_MS = 300;

    // ==================== AUTH ====================
    
    if (!current_staff?.id) {
        signout();
    }

    async function signout() {
        setLoading(true);
        try {
            await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}logout`,
                headers: { Authorization: `Bearer ${current_staff?.token}` },
            });
        } catch (error) {
            // Error during logout
        } finally {
            await dispatch(signOutUser());
            window.location.replace("/login");
        }
    }

    // ==================== INITIAL DATA FETCH ====================
    
    // Fetch balance
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}balance`)
            .then((response) => {
                setMbBalance(response.data.amount || "Not Messagebird");
            })
            .catch((error) => {
                if (error.response?.status === 401) signout();
            });
    }, []);

    // Fetch navbar structure from API (don't wait for token - axios interceptors handle auth)
    useEffect(() => {
        axios.defaults.baseURL = process.env.REACT_APP_API_URL;
        
        axios.get(`${process.env.REACT_APP_API_URL}navbar/dynamic-data`)
            .then((response) => {
                setNavbarFolders(response.data.navbar_folders || []);
                setUnderPropertyFolders(response.data.under_property_folders || []);
                setNavbarProperties(response.data.navbar_properties || []);
                setFolderTags(response.data.folder_tags || {});
                setFolderCounts(response.data.folder_counts || {});
                setTagCounts(response.data.tag_counts || {});
                setPropertyCounts(response.data.property_counts || {});
                setFolderPropertyAssignments(response.data.folder_property_assignments || {});
                setOrphanedCount(response.data.orphaned_count || 0);
                setOrphanedRedCount(response.data.orphaned_red_count || 0);
                setOrphanedOrangeCount(response.data.orphaned_orange_count || 0);
                setDataLoaded(true);
            })
            .catch((error) => {
                console.error("NavBarDynamic: Error fetching structure:", error);
                // Fallback: still mark as loaded so UI shows
                setDataLoaded(true);
            });
    }, []);

    // ==================== WEBSOCKET SUBSCRIPTIONS ====================
    
    useEffect(() => {
        if (!current_staff?.id) return;

        axios.defaults.baseURL = process.env.REACT_APP_API_URL;

        // Initialize Echo (same as original NavBar)
        if (!echoRef.current) {
            if (process.env.REACT_APP_REVERB_APP_ID) {
                echoRef.current = new Echo({
                    broadcaster: "reverb",
                    key: process.env.REACT_APP_REVERB_APP_KEY,
                    wsHost: process.env.REACT_APP_REVERB_URL,
                    wssHost: process.env.REACT_APP_REVERB_URL,
                    wsPort: process.env.REACT_APP_REVERB_PORT,
                    wssPort: process.env.REACT_APP_REVERB_PORT,
                    disableStats: true,
                    encrypted: process.env.REACT_APP_REVERB_SCHEME === "https",
                    forceTLS: process.env.REACT_APP_REVERB_SCHEME === "https",
                    enableLogging: true,
                });
            } else {
                echoRef.current = new Echo({
                    broadcaster: "pusher",
                    key: process.env.REACT_APP_MIX_ABLY_PUBLIC_KEY,
                    wsHost: process.env.REACT_APP_MIX_ABLY_URL,
                    wssHost: process.env.REACT_APP_MIX_ABLY_URL,
                    wsPort: process.env.REACT_APP_MIX_ABLY_PORT,
                    wssPort: process.env.REACT_APP_MIX_ABLY_PORT,
                    disableStats: true,
                    encrypted: false,
                    enabledTransports: ["ws", "wss"],
                    forceTLS: false,
                    enableLogging: true,
                });
            }
        }

        const echo = echoRef.current;

        // Subscribe to channels.list for folder counts (same as original NavBar)
        echo.channel("channels.list")
            .subscribed(() => {})
            .listen(".channels.list", (data) => {
                const now = Date.now();
                if (now - debounceRef.current.channels < DEBOUNCE_MS) return;
                debounceRef.current.channels = now;
                
                // Build folder counts from the data
                const newFolderCounts = {};
                data.forEach((item) => {
                    if (item.name && item.href) {
                        newFolderCounts[item.name] = {
                            count: item.count || 0,
                            red_count: item.red || 0,
                            orange_count: item.countOrange || 0,
                        };
                    }
                });
                setFolderCounts(prev => ({ ...prev, ...newFolderCounts }));
            });

        // Subscribe to tags.list for tag counts (same as original NavBar)
        echo.channel("tags.list")
            .subscribed(() => {})
            .listen(".tags.list", (data) => {
                const now = Date.now();
                if (now - debounceRef.current.tags < DEBOUNCE_MS) return;
                debounceRef.current.tags = now;
                
                // Build tag counts from the data
                const newTagCounts = {};
                Object.values(data).forEach((tag) => {
                    if (tag.id) {
                        newTagCounts[tag.id] = {
                            count: tag.count || 0,
                            red_count: tag.red || 0,
                            orange_count: tag.countOrange || 0,
                        };
                    }
                });
                setTagCounts(prev => ({ ...prev, ...newTagCounts }));
            });

        // Subscribe to mentions (same as original NavBar)
        echo.channel(`mentionList.${current_staff?.id}`)
            .subscribed(() => {})
            .listen("MentionEvent", (data) => {
                const opened = data[0][0].count;
                const closed = data[1][0].count;
                const total = opened + closed;
                setTotalMention(opened + " | " + total);
            });

        // Dispatch Redux actions (same as original NavBar)
        if (current_staff) {
            dispatch(getStaff());
            dispatch(getProperties());
            dispatch(getTags());
            dispatch(getFolders());
            dispatch(getCollections());
            dispatch(getAppointments());
            dispatch(getManagers());
            dispatch(getSettings());
            dispatch(getFlaggedIds());
            dispatch(getBooking());
            dispatch(getTemplates());
            dispatch(getWhatsappTemplates());
            dispatch(getTemplateCategories());
            dispatch(getChatTags());
            setLoading(false);
        }

        return () => {
            if (echoRef.current) {
                echoRef.current.disconnect();
                echoRef.current = null;
            }
        };
    }, [current_staff?.id]); // Changed from token to id since token is undefined

    // Subscribe to chat tag counts when chatTags changes
    useEffect(() => {
        if (!echoRef.current || !chatTags || chatTags.length === 0) return;

        const echo = echoRef.current;
        const subscriptions = [];

        chatTags.forEach((tag) => {
            if (tag.active && tag.show_in_navbar) {
                // Fetch initial count
                axios.get(`${process.env.REACT_APP_API_URL}chat-tags/${tag.id}/count`)
                    .then((response) => {
                        const opened = response.data.opened || 0;
                        const closed = response.data.closed || 0;
                        const total = opened + closed;
                        setChatTagCounts(prev => ({
                            ...prev,
                            [tag.id]: opened + " | " + total
                        }));
                    })
                    .catch((error) => {
                        console.error(`Error fetching count for tag ${tag.id}:`, error);
                    });
                
                // Subscribe to real-time updates
                const channel = echo.channel(`chatTag.${tag.id}`)
                    .subscribed(() => {})
                    .listen("ChatTagEvent", (data) => {
                        const opened = data[0][0].count;
                        const closed = data[1][0].count;
                        const total = opened + closed;
                        setChatTagCounts(prev => ({
                            ...prev,
                            [tag.id]: opened + " | " + total
                        }));
                    });
                subscriptions.push(channel);
            }
        });

        return () => {
            // Cleanup: leave channels when component unmounts or chatTags changes
            subscriptions.forEach(channel => {
                if (channel) {
                    echo.leave(channel.name);
                }
            });
        };
    }, [chatTags]);

    // ==================== NAVIGATION CONFIG ====================
    
    const navigationYou = [
        { name: "Mentions", icon: faAt, href: "/booking/mentions/0", count: totalMention || "0 | 0" },
    ];

    let settings_navigation = [];
    if (!current_staff?.building_manager) {
        if (!current_staff?.is_plumber && !current_staff?.is_electrician) {
            if (!process.env.REACT_APP_HIDE_CHAT_TABS || process.env.REACT_APP_HIDE_CHAT_TABS === "false") {
                settings_navigation.push({ name: "Orders", icon: faBagShopping, href: "/orders/landing" });
            }
            settings_navigation.push({ name: "Reports", icon: faFileChartColumn, href: "/reports/landing" });
            settings_navigation.push({ name: "Properties", icon: faBuildingCircleCheck, href: "/properties/step1" });
            if (["QP", "QL", "UC"].includes(process.env.REACT_APP_COLOUR) && current_staff?.status === "Admin") {
                settings_navigation.push({ name: "Settings", icon: faCogs, href: "/settings" });
                settings_navigation.push({ name: "Settings New", icon: faCogs, href: "/settings_new" });
                settings_navigation.push({ name: "Users", icon: faUser, href: "/settings_users/0" });
                settings_navigation.push({ name: "Live Dashboard", icon: faRobot, href: "/reports/live_dashboard" });
            }
            if (process.env.REACT_APP_COLOUR === "QL") {
                settings_navigation.push({ name: "Meter Readings", icon: faMeterBolt, href: "/meterreadings" });
            }
        }
        settings_navigation.push({ name: "Calendar", icon: faCalendar, href: "/calendar/landing" });
    } else {
        if (current_staff.create_order == 1) {
            settings_navigation.push({ name: "Orders", icon: faBagShopping, href: "/orders/landing" });
        }
        settings_navigation.push({ name: "Properties", icon: faBuildingCircleCheck, href: "/properties/step1" });
        settings_navigation.push({ name: "Calendar", icon: faCalendar, href: "/calendar/landing" });
        settings_navigation.push({ name: "Settings", icon: faCogs, href: "/settings" });
    }

    // ==================== HELPER FUNCTIONS ====================
    
    // Check if a folder should be shown for a specific property
    const folderAppliesToProperty = (folder, propertyId) => {
        const assignments = folderPropertyAssignments[folder.id];
        // If no assignments, folder applies to all properties
        if (!assignments || assignments.length === 0) {
            return true;
        }
        // Check if this property is in the assignments
        return assignments.includes(propertyId);
    };

    // Get folders for a specific property (filtered by assignments)
    const getFoldersForProperty = (propertyId) => {
        return underPropertyFolders.filter(folder => folderAppliesToProperty(folder, propertyId));
    };
    
    // Get counts for a folder (by name or id), optionally for a specific property
    const getFolderCounts = (folder, propertyId = null) => {
        // If propertyId is provided, get counts from propertyCounts
        if (propertyId && propertyCounts[propertyId]?.folders?.[folder.id]) {
            return propertyCounts[propertyId].folders[folder.id];
        }
        // Try by folder name first (from websocket data)
        if (folderCounts[folder.name]) {
            return folderCounts[folder.name];
        }
        // Try by folder id (from API data)
        if (folderCounts[folder.id]) {
            return folderCounts[folder.id];
        }
        return { count: 0, red_count: 0, orange_count: 0 };
    };

    // Get counts for a tag, optionally for a specific property
    const getTagCounts = (tagId, folderId = null, propertyId = null) => {
        // If propertyId is provided, get counts from propertyCounts
        if (propertyId && folderId && propertyCounts[propertyId]?.folders?.[folderId]?.tags?.[tagId]) {
            return propertyCounts[propertyId].folders[folderId].tags[tagId];
        }
        return tagCounts[tagId] || { count: 0, red_count: 0, orange_count: 0 };
    };

    // Build href with optional params
    const buildHref = (baseHref, tagId = null, propId = null) => {
        let href = baseHref;
        const params = new URLSearchParams();
        if (propId) params.set("property_id", propId);
        if (tagId) params.set("tag", tagId);
        if (params.toString()) href += `?${params.toString()}`;
        return href;
    };

    // Get FontAwesome icon component from icon name
    const getIconComponent = (iconName) => {
        if (!iconName) return null;
        
        // Convert icon name to camelCase format (e.g., "wrench" -> "faWrench")
        const camelCaseIcon = 'fa' + iconName.charAt(0).toUpperCase() + iconName.slice(1).replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        
        // Try to find icon in pro-regular first, then pro-solid
        const icon = ProRegularIcons[camelCaseIcon] || ProSolidIcons[camelCaseIcon];
        return icon;
    };

    // Check if a folder/tag is currently selected (considering property_id)
    const isSelected = (folderHref, tagId = null, propId = null) => {
        // Must match the pathname
        if (location.pathname !== folderHref) return false;
        
        // Check property_id match (convert to string for comparison)
        const currentPropId = property_id ? String(property_id) : null;
        const checkPropId = propId ? String(propId) : null;
        if (currentPropId !== checkPropId) return false;
        
        // Check tag_id match
        if (tagId === null) {
            // "All" link - should only be selected if no tag is selected
            return !tag_id;
        } else {
            // Specific tag - check if it matches
            return String(tag_id) === String(tagId);
        }
    };

    // ==================== RENDER HELPERS ====================
    
    // Render a folder with its tags
    const renderFolder = (folder, isUnderProperty = false, propertyId = null) => {
        const counts = getFolderCounts(folder, propertyId);
        const hasTags = folder.has_tags === 1 || folder.has_tags === true;
        const tags = folderTags[folder.id] || [];
        
        // Auto-expand folder if current URL matches this folder (with matching property_id if applicable)
        const shouldFolderBeOpen = location.pathname === folder.href && 
            (propertyId ? String(property_id) === String(propertyId) : !property_id);

        if (hasTags && tags.length > 0) {
            return (
                <Disclosure as="div" className="space-y-1" key={`folder_${folder.id}_${propertyId || "main"}`} defaultOpen={shouldFolderBeOpen}>
                    {({ open }) => (
                        <>
                            <Disclosure.Button className="text-divider border-b-2 border-gray-400 text-sm group w-full flex items-center pr-3 py-2 text-left focus:outline-none">
                                <svg
                                    className={classNames(
                                        open ? "rotate-90" : "",
                                        "text-divider mr-3 flex-shrink-0 h-5 w-5 transform group-hover:text-gray-400 transition-colors ease-in-out duration-150"
                                    )}
                                    viewBox="0 0 20 20"
                                    aria-hidden="true"
                                >
                                    <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
                                </svg>
                                <span className="flex-1 font-light text-xs">{(folder.display_name || folder.name).toUpperCase()}</span>
                                <span className="text-right min-w-[40px] text-xs">
                                    {counts.red_count > 0 && <span className="text-red-400">{counts.red_count} |&nbsp;</span>}
                                    {counts.orange_count > 0 && <span className="text-yellow-400">{counts.orange_count} |&nbsp;</span>}
                                    <span className="text-green-400">{counts.count || 0}</span>
                                </span>
                            </Disclosure.Button>
                            <Transition
                                enter="transition duration-100 ease-out"
                                enterFrom="transform scale-95 opacity-0"
                                enterTo="transform scale-100 opacity-100"
                                leave="transition duration-75 ease-out"
                                leaveFrom="transform scale-100 opacity-100"
                                leaveTo="transform scale-95 opacity-0"
                            >
                                <Disclosure.Panel className="space-y-1">
                                    {/* All items link - show untagged count only (tagged bookings shown under their tags) */}
                                    <Link
                                        to={buildHref(folder.href, null, propertyId)}
                                        className={classNames(
                                            isSelected(folder.href, null, propertyId) ? "bg-white text-dark_background" : "hover:bg-white text-white",
                                            "group flex items-center rounded-l-3xl pl-4 pr-3 py-2 text-sm font-medium transition ease-linear delay-50 hover:text-dark_background"
                                        )}
                                    >
                                        <span className="flex-1 font-light">All {folder.display_name || folder.name}</span>
                                        <span className="text-right min-w-[40px] text-xs">
                                            {(counts.untagged_red || 0) > 0 && <span className="text-red-400">{counts.untagged_red} |&nbsp;</span>}
                                            {(counts.untagged_orange || 0) > 0 && <span className="text-yellow-400">{counts.untagged_orange} |&nbsp;</span>}
                                            {(counts.untagged_count || counts.count || 0) > 0 && <span className="text-green-400">{counts.untagged_count ?? counts.count}</span>}
                                        </span>
                                    </Link>

                                    {/* Tags */}
                                    {tags.map((tag) => {
                                        const tagCnts = getTagCounts(tag.id, folder.id, propertyId);
                                        return (
                                            <Link
                                                key={`tag_${tag.id}_${propertyId || "main"}`}
                                                to={buildHref(folder.href, tag.id, propertyId)}
                                                className={classNames(
                                                    isSelected(folder.href, tag.id, propertyId) ? "bg-white text-dark_background" : "hover:bg-white text-white",
                                                    "group flex items-center rounded-l-3xl pl-4 pr-3 py-2 text-sm font-medium transition ease-linear delay-50 hover:text-dark_background"
                                                )}
                                            >
                                                <span className="flex-1 font-light">{tag.name}</span>
                                                <span className="text-right min-w-[40px] text-xs">
                                                    {tagCnts.red_count > 0 && <span className="text-red-400">{tagCnts.red_count} |&nbsp;</span>}
                                                    {tagCnts.orange_count > 0 && <span className="text-yellow-400">{tagCnts.orange_count} |&nbsp;</span>}
                                                    {tagCnts.count > 0 && <span className="text-green-400">{tagCnts.count}</span>}
                                                </span>
                                            </Link>
                                        );
                                    })}

                                    {/* Orphaned/Unlinked */}
                                    <Link
                                        to={buildHref(folder.href, "orphaned", propertyId)}
                                        className={classNames(
                                            isSelected(folder.href, "orphaned", propertyId) ? "bg-white text-dark_background" : "hover:bg-white text-white",
                                            "group flex items-center rounded-l-3xl pl-4 pr-3 py-2 text-sm font-medium transition ease-linear delay-50 hover:text-dark_background"
                                        )}
                                    >
                                        <span className="flex-1 font-light">Unlinked</span>
                                    </Link>
                                </Disclosure.Panel>
                            </Transition>
                        </>
                    )}
                </Disclosure>
            );
        }

        // Simple folder without tags - just a link
        return (
            <Link
                key={`folder_${folder.id}_${propertyId || "main"}`}
                to={buildHref(folder.href, null, propertyId)}
                className={classNames(
                    isSelected(folder.href, null, propertyId) ? "bg-white text-dark_background" : "hover:bg-white text-white",
                    "group flex items-center rounded-l-3xl pl-4 pr-3 py-2 text-sm font-medium transition ease-linear delay-50 hover:text-dark_background"
                )}
            >
                <span className="flex-1 font-light">{folder.display_name || folder.name}</span>
                <span className="text-right min-w-[40px] text-xs">
                    {counts.red_count > 0 && <span className="text-red-400">{counts.red_count} |&nbsp;</span>}
                    {counts.orange_count > 0 && <span className="text-yellow-400">{counts.orange_count} |&nbsp;</span>}
                    {counts.count > 0 && <span className="text-green-400">{counts.count}</span>}
                </span>
            </Link>
        );
    };

    // Render a property with its under_property folders
    const renderProperty = (property) => {
        const propCounts = propertyCounts[property.id] || { total_count: 0, total_red: 0 };
        // Auto-expand if this property matches the current property_id in URL
        const shouldBeOpen = property_id && String(property.id) === String(property_id);

        return (
            <Disclosure as="div" className="space-y-1" key={`property_${property.id}`} defaultOpen={shouldBeOpen}>
                {({ open }) => (
                    <>
                        <Disclosure.Button className="text-divider border-b-2 border-gray-400 text-sm group w-full flex items-center pr-3 py-2 text-left focus:outline-none">
                            <svg
                                className={classNames(
                                    open ? "rotate-90" : "",
                                    "text-divider mr-3 flex-shrink-0 h-5 w-5 transform group-hover:text-gray-400 transition-colors ease-in-out duration-150"
                                )}
                                viewBox="0 0 20 20"
                                aria-hidden="true"
                            >
                                <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
                            </svg>
                            <FontAwesomeIcon icon={faBuilding} className="mr-2 h-4 w-4 text-divider" />
                            <span className="flex-1 font-light text-xs">{property.name.toUpperCase()}</span>
                            <span className="text-right min-w-[40px] text-xs">
                                {propCounts.total_red > 0 && <span className="text-red-400">{propCounts.total_red}</span>}
                            </span>
                        </Disclosure.Button>
                        <Transition
                            enter="transition duration-100 ease-out"
                            enterFrom="transform scale-95 opacity-0"
                            enterTo="transform scale-100 opacity-100"
                            leave="transition duration-75 ease-out"
                            leaveFrom="transform scale-100 opacity-100"
                            leaveTo="transform scale-95 opacity-0"
                        >
                            <Disclosure.Panel className="space-y-1 pl-4">
                                {getFoldersForProperty(property.id).map((folder) => renderFolder(folder, true, property.id))}
                            </Disclosure.Panel>
                        </Transition>
                    </>
                )}
            </Disclosure>
        );
    };

    // ==================== RENDER ====================
    
    if (loading) return <AppLoader />;

    return (
        <>
            {/* Mobile sidebar */}
            <Transition.Root show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="fixed inset-0 flex z-40 md:hidden" onClose={setSidebarOpen}>
                    <Transition.Child as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
                    </Transition.Child>
                    <Transition.Child as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="-translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="-translate-x-full">
                        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-black">
                            <Transition.Child as={Fragment} enter="ease-in-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in-out duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
                                <div className="absolute top-0 right-0 -mr-12 pt-2">
                                    <button type="button" className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)}>
                                        <span className="sr-only">Close sidebar</span>
                                        <FontAwesomeIcon className="h-6 w-6 text-white" aria-hidden="true" icon={faTimes} />
                                    </button>
                                </div>
                            </Transition.Child>
                            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                                <div className="flex-shrink-0 flex flex-col items-center px-4 gap-2">
                                    <VersionBanner variant="inline" />
                                    <Link to="/">
                                        <ApplicationLogo className="h-12 w-auto text-white" square />
                                    </Link>
                                </div>
                                <nav className="mt-5 px-2 space-y-1">
                                    {current_staff?.hideSearch != 1 && (
                                        <div className="flex text-white">
                                            <button
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            modal_type: "ModalSearch",
                                                        }),
                                                    )
                                                }
                                                type="button"
                                                className="hover:bg-white hover:rounded-3xl text-white group flex items-center pr-3 py-2 text-sm font-medium hover:text-dark_background"
                                            >
                                                <FontAwesomeIcon className="mr-3 w-6 flex-shrink-0 text-primary" aria-hidden="true" icon={faSearch} />
                                                <span className="flex-1 font-light">Search</span>
                                            </button>
                                        </div>
                                    )}
                                    {settings_navigation.map((item) => (
                                        <a key={"mob_" + item.name} href={item.href} className="group flex items-center px-2 py-2 text-sm font-medium rounded-md hover:bg-primary_hover hover:text-white">
                                            <span className="flex-1 text-gray-200">{item.name}</span>
                                        </a>
                                    ))}
                                </nav>
                            </div>
                            <div className="flex-shrink-0 flex bg-black p-4">
                                <div className="flex items-center">
                                    <ProfilePicture id={current_staff?.id} />
                                    <div className="ml-1">
                                        <p className="text-sm text-divider font-light uppercase">{current_staff?.name} {current_staff?.surname}</p>
                                        {login_method && (
                                            <p className="text-[10px] font-semibold uppercase tracking-wide">
                                                <span className={login_method === "sso" ? "text-green-400" : "text-amber-400"}>
                                                    {login_method === "sso" ? "● Signed in via SSO" : "● Signed in via password"}
                                                </span>
                                            </p>
                                        )}
                                        <button className="text-xs text-divider font-light uppercase" onClick={() => signout()}>Sign Out</button>
                                        <p className="text-sm text-divider font-light uppercase">V {FRONTEND_VERSION}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                    <div className="flex-shrink-0 w-14" />
                </Dialog>
            </Transition.Root>

            {/* Desktop sidebar */}
            <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
                <div className="flex-1 flex flex-col pl-6 rounded-3xl min-h-0 bg-dark_background">
                    <div className="flex-1 flex flex-col pt-2 pb-4 overflow-y-auto">
                        {/* Logo */}
                        <div className="flex flex-col items-center justify-center gap-2 py-4 pr-4">
                            <VersionBanner variant="inline" />
                            {process.env.REACT_APP_COLOUR === "UC" ? (
                                <ApplicationLogo className="h-14 w-auto text-white" />
                            ) : process.env.REACT_APP_COLOUR === "QL" ? (
                                <ApplicationLogo className="h-24 w-auto text-white" square color="white" />
                            ) : (
                                <Link to="/">
                                    <ApplicationLogo className="h-14 w-auto text-white" square />
                                </Link>
                            )}
                        </div>

                        <nav className="mt-5 flex-1 space-y-1">
                            {/* Credits */}
                            {current_staff?.status === "Admin" && mbBalance && mbBalance !== "Not Messagebird" && (
                                <p className="text-white">
                                    Credits: <span className={mbBalance > 0 ? "text-green-400" : "text-red-400"}>$ {mbBalance}</span>
                                </p>
                            )}

                            {/* Search */}
                            {current_staff?.hideSearch != 1 && (
                                <div className="flex text-white">
                                    <button
                                        onClick={() => dispatch(openModal({ modal_type: "ModalSearch" }))}
                                        type="button"
                                        className="hover:bg-white hover:rounded-3xl text-white group flex items-center pr-3 py-2 text-sm font-medium hover:text-dark_background"
                                    >
                                        <FontAwesomeIcon className="mr-3 w-6 flex-shrink-0 text-primary" icon={faSearch} />
                                        <span className="flex-1 font-light">Search</span>
                                    </button>
                                </div>
                            )}

                            {/* Mentions */}
                            <div className="text-white">
                                {navigationYou.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        className={classNames(
                                            location.pathname === item.href ? "bg-white text-dark_background" : "hover:bg-white text-white",
                                            "group flex items-center rounded-l-3xl pr-3 py-2 text-sm font-medium transition ease-linear delay-50 hover:text-dark_background"
                                        )}
                                    >
                                        <FontAwesomeIcon className="mr-3 w-6 flex-shrink-0 text-primary" icon={item.icon} />
                                        <span className="flex-1 font-light">{item.name}</span>
                                        <span className="ml-3 inline-block py-1 px-3 text-xs">{totalMention}</span>
                                    </Link>
                                ))}
                            </div>

                            {/* Chat Tags */}
                            {chatTags && chatTags.length > 0 && chatTags.filter(tag => tag.active && tag.show_in_navbar).map((tag) => {
                                const icon = getIconComponent(tag.icon);
                                return (
                                    <div key={`chat-tag-${tag.id}`} className="text-white">
                                        <Link
                                            to={`/booking/chat-tags/${tag.id}`}
                                            className={classNames(
                                                location.pathname === `/booking/chat-tags/${tag.id}` ? "bg-white text-dark_background" : "hover:bg-white text-white",
                                                "group flex items-center rounded-l-3xl pr-3 py-2 text-sm font-medium transition ease-linear delay-50 hover:text-dark_background"
                                            )}
                                        >
                                            <div className="mr-3 w-6 h-6 flex-shrink-0 rounded-full bg-primary flex items-center justify-center text-white text-xs">
                                                {icon ? (
                                                    <FontAwesomeIcon icon={icon} className="h-3 w-3" />
                                                ) : (
                                                    tag.name.substring(0, 2).toUpperCase()
                                                )}
                                            </div>
                                            <span className="flex-1 font-light">{tag.name}</span>
                                            <span className="ml-3 inline-block py-1 px-3 text-xs">
                                                {chatTagCounts[tag.id] || "0 | 0"}
                                            </span>
                                        </Link>
                                    </div>
                                );
                            })}

                            {/* Resources */}
                            <div className="text-white">
                                <a href="/admin/resources" target="_blank" rel="noopener noreferrer" className="hover:bg-white text-white group flex items-center rounded-l-3xl pr-3 py-2 text-sm font-medium transition ease-linear delay-50 hover:text-dark_background">
                                    <FontAwesomeIcon className="mr-3 w-6 flex-shrink-0 text-primary" icon={faBooks} />
                                    <span className="flex-1 font-light">Resources</span>
                                </a>
                            </div>

                            {/* ========== DYNAMIC NAVBAR ITEMS ========== */}
                            {navbarFolders.map((folder) => {
                                // Property container - render properties here
                                if (folder.is_property_container === 1 || folder.is_property_container === true) {
                                    return (
                                        <React.Fragment key={`prop_container_${folder.id}`}>
                                            {navbarProperties.map((property) => renderProperty(property))}
                                        </React.Fragment>
                                    );
                                }
                                // Regular folder
                                return renderFolder(folder);
                            })}

                            {/* Orphaned Bookings */}
                            <Link
                                to="/booking/orphaned"
                                className={classNames(
                                    location.pathname === "/booking/orphaned" ? "bg-white text-dark_background" : "hover:bg-white text-white",
                                    "group flex items-center rounded-l-3xl pl-4 pr-3 py-2 text-sm font-medium transition ease-linear delay-50 hover:text-dark_background"
                                )}
                            >
                                <span className="flex-1 font-light">Orphaned</span>
                                <span className="text-right min-w-[40px] text-xs">
                                    {orphanedRedCount > 0 && <span className="text-red-400">{orphanedRedCount} |&nbsp;</span>}
                                    {orphanedOrangeCount > 0 && <span className="text-yellow-400">{orphanedOrangeCount} |&nbsp;</span>}
                                    <span className="text-green-400">{orphanedCount}</span>
                                </span>
                            </Link>

                            {/* ========== END DYNAMIC NAVBAR ITEMS ========== */}

                            {/* Staff Online/Offline */}
                            {staff && staff.length > 0 && (
                                <>
                                    <div className="py-4">
                                        <p className="font-light text-divider border-gray-400 border-b-2 pb-2 text-xs">ONLINE</p>
                                        <div className="py-2 flex space-x-2 flex-wrap">
                                            <StaffOnline type="online" staff={staff} styling="-mr-6" />
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Settings Navigation */}
                            {current_staff?.hideSettings != 1 && (
                                <>
                                    {!current_staff?.building_manager && !current_staff?.is_plumber && !current_staff?.is_electrician && (
                                        <button
                                            onClick={() => dispatch(openModal({ modal_type: "ModalCreateApplication" }))}
                                            type="button"
                                            className="hover:bg-white hover:rounded-3xl text-white group flex items-center pr-3 py-2 text-sm font-medium hover:text-dark_background"
                                        >
                                            <FontAwesomeIcon className="mr-3 w-6 flex-shrink-0 text-primary" icon={faMessagePlus} />
                                            <span className="flex-1 font-light">Create a Chat</span>
                                        </button>
                                    )}
                                    {settings_navigation.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className={classNames(
                                                location.pathname === item.href ? "bg-white rounded-l-3xl text-dark_background" : "hover:bg-white hover:rounded-l-3xl text-white",
                                                "group flex items-center pr-3 py-2 text-sm font-medium hover:text-dark_background"
                                            )}
                                        >
                                            <FontAwesomeIcon className="mr-3 w-6 flex-shrink-0 text-primary" icon={item.icon} />
                                            <span className="flex-1 font-light">{item.name}</span>
                                        </Link>
                                    ))}
                                    <a className="hover:bg-white hover:rounded-l-3xl text-white group flex items-center pr-3 py-2 text-sm font-medium hover:text-dark_background" href="https://forms.clickup.com/9012594561/f/8ck2hw1-1412/ZXA7LJUYVDE7HBDMS3" target="_blank">
                                        <FontAwesomeIcon className="mr-3 w-6 flex-shrink-0 text-primary" icon={faBug} />
                                        <span className="flex-1 font-light">Log a Bug</span>
                                    </a>
                                </>
                            )}
                        </nav>
                    </div>

                    {/* Profile section */}
                    <div className="pt-6 pb-2 border-b-2 border-gray-400" />
                    <div className="flex-shrink-0 flex py-4">
                        <a href="#" className="flex-shrink-0 w-full group block">
                            <div className="flex items-center">
                                <ProfilePicture id={current_staff?.id} />
                                <div className="ml-1">
                                    <p className="text-sm text-divider font-light uppercase">
                                        {current_staff?.name} {current_staff?.surname} {current_staff?.status === "Admin" && ` (${current_staff?.status})`}
                                    </p>
                                    {login_method && (
                                        <p className="text-[10px] font-semibold uppercase tracking-wide">
                                            <span className={login_method === "sso" ? "text-green-400" : "text-amber-400"}>
                                                {login_method === "sso" ? "● Signed in via SSO" : "● Signed in via password"}
                                            </span>
                                        </p>
                                    )}
                                    <button className="text-xs text-divider font-light group-hover:text-gray-200 uppercase" onClick={() => signout()}>Sign Out</button>
                                    <p className="text-sm text-divider font-light uppercase">V {FRONTEND_VERSION}</p>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </div>

            {/* Mobile header */}
            <div className="md:pl-64 flex flex-col flex-1">
                <div className="sticky top-0 z-10 md:hidden px-4 pt-4 sm:pl-3 sm:pt-3 bg-black flex">
                    {process.env.REACT_APP_COLOUR === "UC" ? (
                        <img className="h-12 w-auto mx-auto" src="/images/logo_uc.svg" alt="Urban Circle" />
                    ) : process.env.REACT_APP_COLOUR === "QL" ? (
                        <img className="h-12 w-auto mx-auto" src="/images/QL_Logo_final_white.png" alt="Quorum Living" />
                    ) : (
                        <img className="h-12 w-auto mx-auto" src="/images/logo-qholdings-v2.png" alt="Quorum Properties" />
                    )}
                    <button type="button" className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset" onClick={() => setSidebarOpen(true)}>
                        <span className="sr-only">Open sidebar</span>
                        <FontAwesomeIcon className="h-6 w-6 text-white" icon={faBars} />
                    </button>
                </div>
            </div>
        </>
    );
}
